import { useState, useEffect, useCallback } from 'react';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { useSelector } from 'react-redux';

import { v4 } from 'uuid';

import { PusherService } from '../services/pusher-service';
import { CONNECT_RESPONSE_SCALE_READ } from '../services/pusher-service/pusher-contract-messages';
import { shipstationConnect } from '../services/shipstation-connect';
import { UnitConversionService } from '../services/unit-conversion-service';
import { WeightUnit } from '../enums/weight-unit';
import Global from '../store/global-selectors';
import { DimensionUnit } from '../enums/dimension-unit';
import { convertConnectWeightToOunces } from '../services/shipstation-connect/connect-scales';
import { useIsPusherReady } from './use-pusher-event';

const getWeightFromMessage = (msg, sellerWeightUnit) => {
  const { lbs, oz } = msg.weight;
  const weight = convertConnectWeightToOunces(lbs, oz);

  const convertedWeight = UnitConversionService.convertValue(weight, WeightUnit.Ounces, sellerWeightUnit);
  return {
    value: convertedWeight,
    unit: sellerWeightUnit,
  };
};

const getDimensionsFromMessage = (msg, sellerDimensionUnit) => {
  if (!msg.dims) {
    return null;
  }

  return {
    unit: sellerDimensionUnit.toLowerCase(),
    length: UnitConversionService.convertValue(msg.dims.length, DimensionUnit.Inches, sellerDimensionUnit),
    width: UnitConversionService.convertValue(msg.dims.width, DimensionUnit.Inches, sellerDimensionUnit),
    height: UnitConversionService.convertValue(msg.dims.height, DimensionUnit.Inches, sellerDimensionUnit),
  };
};

const createScaleReadObservable = (clientRequestId) => {
  return new Observable((observer) => {
    const handler = (msg) => observer.next(msg);
    const unbindFromEventOnAllChannels = PusherService.bindToEventOnAllChannels(CONNECT_RESPONSE_SCALE_READ, handler);
    return () => unbindFromEventOnAllChannels();
  }).pipe(
    filter((pusherMsg) => {
      const { requestId } = pusherMsg;
      return requestId === clientRequestId;
    }),
    take(1),
  );
};

export const useReadFromScale = (onScaleRead) => {
  const [clientRequestId, setClientRequestId] = useState(v4());
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);

  const { isSubscribedToMainSellerChannel } = useIsPusherReady();

  useEffect(() => {
    if (!isSubscribedToMainSellerChannel) {
      return;
    }

    const obs$ = createScaleReadObservable(clientRequestId);
    const sub = obs$.subscribe((message) => {
      setClientRequestId(v4());
      if (!message?.weight) {
        return;
      }

      const weightAndDims = {
        weight: getWeightFromMessage(message, sellerWeightUnit),
        dims: getDimensionsFromMessage(message, sellerDimensionUnit),
      };

      onScaleRead(weightAndDims);
    });
    return () => sub.unsubscribe();
  }, [onScaleRead, clientRequestId, sellerWeightUnit, sellerDimensionUnit, isSubscribedToMainSellerChannel]);

  const onReadFromScale = useCallback(
    (scale) => {
      shipstationConnect.readFromScale(scale, clientRequestId);
    },
    [clientRequestId],
  );

  return onReadFromScale;
};
